import type { KeyboardEvent, MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import { useJobs } from '../api/jobs';
import { InlineError } from './InlineError';
import { Skeleton } from './Skeleton';
import { StateBadge } from './StateBadge';
import { RelativeTime } from './RelativeTime';
import { JiraLink } from './JiraLink';
import type { Job } from '../types';

// attentive-mobile is the default org for agent repos, so strip that owner prefix
// as noise — but keep the full owner/name for any other org so it isn't misleading.
const DEFAULT_OWNER_PREFIX = 'attentive-mobile/';

function displayRepo(repo: string): string {
  return repo.startsWith(DEFAULT_OWNER_PREFIX) ? repo.slice(DEFAULT_OWNER_PREFIX.length) : repo;
}

// repo is "owner/name"; GitHub tree URLs take the branch as raw path segments, so
// slashes in a branch name (e.g. a1e/foo) are preserved rather than encoded.
function githubBranchUrl(repo: string, branch: string): string {
  return `https://github.com/${repo}/tree/${branch}`;
}

export function JobsTable({ userId }: { userId: string }) {
  const { data, error, isPending, refetch } = useJobs(userId);

  if (isPending) {
    return <Skeleton variant="rows" rows={5} rowClassName="my-2 h-6" className="px-6 py-4" />;
  }
  if (error) {
    return (
      <InlineError
        heading="Couldn't load jobs"
        error={error}
        onRetry={() => void refetch()}
        className="m-6"
      />
    );
  }
  // Interactive jobs render as cards in <InteractiveSessions/> above; this table
  // is autonomous (PROMPT/ORCA) jobs only.
  const jobs = (data ?? [])
    .filter((j) => j.mode !== 'INTERACTIVE')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  return (
    <section aria-label="Autonomous jobs" className="pt-2">
      {jobs.length === 0 ? (
        <p className="px-6 py-6 text-sm text-slate-500">No autonomous jobs yet.</p>
      ) : (
        <table className="w-full table-fixed text-left text-sm">
          <thead className="bg-[#d9e0ea] text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-36 px-3 py-2">State</th>
              <th className="w-36 px-3 py-2">Mode</th>
              <th className="w-40 px-3 py-2">Repo</th>
              <th className="px-3 py-2">Prompt</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <Row key={j.id} job={j} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Row({ job }: { job: Job }) {
  const navigate = useNavigate();
  const openDrawer = () => {
    void navigate(`/jobs/${job.id}`);
  };

  // The whole row opens the drawer — except clicks on a real link (Jira ticket,
  // branch) or while selecting text, which keep their own behavior.
  const handleClick = (e: MouseEvent<HTMLTableRowElement>) => {
    if ((e.target as HTMLElement).closest('a,button')) return;
    if (window.getSelection()?.toString()) return;
    openDrawer();
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter') openDrawer();
  };

  return (
    <tr
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className="cursor-pointer align-top odd:bg-white even:bg-slate-50 hover:bg-slate-100"
    >
      <td className="px-3 py-1.5">
        <div className="flex flex-col items-start gap-1">
          <StateBadge state={job.state} />
          <span className="text-xs text-slate-500">
            <RelativeTime iso={job.updatedAt} />
          </span>
        </div>
      </td>
      <td className="px-3 py-1.5">
        <ModeCell job={job} />
      </td>
      <td className="px-3 py-1.5">
        <RepoCell job={job} />
      </td>
      <td className="px-3 py-1.5">
        <p className="line-clamp-2 text-slate-900">{job.prompt || job.id}</p>
      </td>
    </tr>
  );
}

// Repo cell, standardized to the ORCA/ticket two-line shape: the repo as a small
// grey label on top, the git branch as the normal-weight value beneath (branch
// omitted when absent).
function RepoCell({ job }: { job: Job }) {
  if (!job.repo) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span
        className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400"
        title={job.repo}
      >
        {displayRepo(job.repo)}
      </span>
      {job.branch && (
        <a
          href={githubBranchUrl(job.repo, job.branch)}
          target="_blank"
          rel="noopener noreferrer"
          title={job.branch}
          className="truncate text-sm text-slate-700 underline-offset-2 hover:underline"
        >
          {job.branch}
        </a>
      )}
    </div>
  );
}

// Mode cell: PROMPT jobs show a plain label; ORCA jobs show a small "ORCA" tag
// over a link to their Jira ticket. (INTERACTIVE jobs never reach the table —
// they render as cards in <InteractiveSessions/>.)
function ModeCell({ job }: { job: Job }) {
  if (job.mode === 'ORCA') {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          ORCA
        </span>
        {job.jiraIssueKey ? (
          <JiraLink
            issueKey={job.jiraIssueKey}
            className="whitespace-nowrap font-mono text-sm text-slate-700 underline-offset-2 hover:underline"
          />
        ) : (
          <span className="text-sm text-slate-400">—</span>
        )}
      </div>
    );
  }
  // PROMPT has no value line; a placeholder second line keeps its label
  // vertically aligned with the two-line ORCA cells (align-top rows).
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {job.mode ?? '—'}
      </span>
      <span aria-hidden="true" className="text-sm">
        {'\u00A0'}
      </span>
    </div>
  );
}
